// Chunked exposed-face mesher for a VoxelGrid. Each chunk owns its own
// face meshes (one per palette), grid-line LineSegments, and edge-line
// LineSegments — all parented to a chunk group, which is parented to
// the mesher's top-level group.
//
// Mutations on VoxelGrid mark a chunk dirty (plus the neighbor chunk
// when the cell lies on a boundary, since the neighbor's exposed
// faces depend on the changed cell). `sync(grid)` drains the dirty
// set once per frame and re-meshes only those chunks. A drag-paint
// gesture that touches one or two chunks now rebuilds at most ~16³
// cells of geometry per frame instead of the whole grid.
//
// "Naive" face-emit is preserved (each exposed face is its own quad,
// no greedy merging); that's an orthogonal optimization to apply only
// if perf demands.
import * as THREE from "three";
import {
  type PaletteIndex,
  type VoxelGrid,
  parseChunkKey,
} from "../model/voxel-grid";
import { buildChunkGeometry } from "./chunk-geometry";
import type {
  ChunkUpdate,
  MeshResultMsg,
} from "./mesher-protocol";

// Geometry buffers can arrive as plain number[] (main-thread sync path) or as
// transferred typed arrays (worker path) — wrap each without an extra copy when
// it's already typed.
function posAttr(arr: ArrayLike<number>, size: number): THREE.BufferAttribute {
  return arr instanceof Float32Array
    ? new THREE.BufferAttribute(arr, size)
    : new THREE.Float32BufferAttribute(arr as number[], size);
}
function idxAttr(arr: ArrayLike<number>): THREE.BufferAttribute {
  return arr instanceof Uint32Array
    ? new THREE.BufferAttribute(arr, 1)
    : new THREE.Uint32BufferAttribute(arr as number[], 1);
}

// One face mesh's geometry, palette-tagged. pos/norm/idx are number[] (sync) or
// typed arrays (worker).
interface FaceBuffers {
  palette: number;
  pos: ArrayLike<number>;
  norm: ArrayLike<number>;
  idx: ArrayLike<number>;
}

interface ChunkMesh {
  group: THREE.Group;
  faces: Map<PaletteIndex, THREE.Mesh>;
  gridLines: THREE.LineSegments;
  edgeLines: THREE.LineSegments;
}

// Shared with the canvas floor/wall shader in main.ts so cell lines on
// drawn voxels always match cell lines on the canvas surfaces.
export const GRID_LINE_COLOR = 0x404040;
const GRID_LINE_OPACITY = 1.0;
const EDGE_LINE_COLOR = 0x000000;

// Hover-highlight glow for placement mode — warm amber, matching the
// move/selection tint elsewhere so "movable" reads consistently.
const HIGHLIGHT_EMISSIVE = 0xf4b400;
const HIGHLIGHT_INTENSITY = 0.45;

export class VoxelMesher {
  private _group = new THREE.Group();
  private chunks = new Map<number, ChunkMesh>();

  // Materials live on the mesher and are shared across every chunk
  // mesh — palette materials grow on demand; the grid + edge materials
  // are singletons. Sharing keeps both the GPU upload cost and the
  // visibility toggles cheap.
  private paletteMats = new Map<PaletteIndex, THREE.MeshStandardMaterial>();
  private gridMat: THREE.LineBasicMaterial;
  private edgeMat: THREE.LineBasicMaterial;

  // Current visibility toggles — applied to newly-created chunks so
  // their line meshes pick up the right initial state.
  private gridLinesVisible = true;
  private edgesVisible = true;

  // Hover highlight (placement mode): when on, every palette material
  // gets an amber emissive glow so the whole object reads as "grabbable".
  // Stored so palette mats created after the toggle inherit it.
  private highlighted = false;

  // Off-thread meshing. When a worker is supplied, sync() posts the dirty chunks
  // to it and applies the geometry it sends back asynchronously; `outstanding`
  // tracks chunks in flight so callers (the preview shield) know when the build
  // finishes. Without a worker (tests, thumbnailer, model-bake) sync() meshes
  // on the calling thread. onApplied wakes the render loop when async geometry
  // lands (it arrives outside any input event).
  private worker: Worker | null;
  private onApplied: (() => void) | null;
  private outstanding = new Set<number>();

  constructor(
    private palette: PaletteLookup,
    worker: Worker | null = null,
    onApplied: (() => void) | null = null,
  ) {
    this.worker = worker;
    this.onApplied = onApplied;
    if (worker) {
      worker.onmessage = (e: MessageEvent<MeshResultMsg>): void => {
        for (const r of e.data.results) {
          if (r.empty) this.teardownChunk(r.key);
          else this.applyChunkGeometry(r.key, r.faces, r.gridLines, r.edgeLines);
          this.outstanding.delete(r.key);
        }
        this.onApplied?.();
      };
    }
    this.gridMat = new THREE.LineBasicMaterial({
      color: GRID_LINE_COLOR,
      transparent: true,
      opacity: GRID_LINE_OPACITY,
    });
    this.gridMat.depthFunc = THREE.LessEqualDepth;

    // Edge lines: transparent+alpha 1 so they share the transparent
    // pass with grid lines (renderOrder picks the winner); otherwise
    // the translucent grid blends over the silhouette and washes it
    // out where both are enabled.
    this.edgeMat = new THREE.LineBasicMaterial({
      color: EDGE_LINE_COLOR,
      transparent: true,
      opacity: 1.0,
    });
    this.edgeMat.depthFunc = THREE.LessEqualDepth;
  }

  get object3D(): THREE.Object3D {
    return this._group;
  }

  setGridVisible(v: boolean): void {
    this.gridLinesVisible = v;
    for (const chunk of this.chunks.values()) {
      chunk.gridLines.visible = v;
    }
  }

  setEdgesVisible(v: boolean): void {
    this.edgesVisible = v;
    for (const chunk of this.chunks.values()) {
      chunk.edgeLines.visible = v;
    }
  }

  // Zoom fade: scale line opacity by how big a voxel is on screen. The lines
  // are ~1px wide regardless of zoom, so once a voxel shrinks to a few pixels
  // they cover a large fraction of it and darken/muddy the color (worst at
  // grazing angles, where faces foreshorten and the lines bunch up). Fading
  // them out when zoomed out keeps colors vibrant; full opacity returns when
  // zoomed in. Driven per-frame from the active camera (see main's frame step).
  // Independent of the show/hide toggles above — those set mesh.visible, this
  // sets material opacity, so a hidden line stays hidden at any fade. Caller
  // passes already-curved factors in [0,1]; this just applies them (and is a
  // cheap no-op when unchanged, since opacity is a plain uniform).
  setLineFade(gridFade: number, edgeFade: number): void {
    const g = GRID_LINE_OPACITY * gridFade;
    if (this.gridMat.opacity !== g) this.gridMat.opacity = g;
    if (this.edgeMat.opacity !== edgeFade) this.edgeMat.opacity = edgeFade;
  }

  // Toggle the hover-highlight glow on every palette material at once.
  // Idempotent — cheap to call each frame from the pick.
  setHighlighted(v: boolean): void {
    if (this.highlighted === v) return;
    this.highlighted = v;
    for (const mat of this.paletteMats.values()) this.applyHighlight(mat);
  }

  private applyHighlight(mat: THREE.MeshStandardMaterial): void {
    mat.emissive.setHex(this.highlighted ? HIGHLIGHT_EMISSIVE : 0x000000);
    mat.emissiveIntensity = this.highlighted ? HIGHLIGHT_INTENSITY : 1.0;
  }

  // Re-read every cached palette material's color from the palette table.
  // Materials are shared across this mesher's chunks, so one update recolors
  // all existing faces without re-meshing. Call after the palette is edited.
  refreshPaletteColors(): void {
    for (const [p, mat] of this.paletteMats) mat.color.copy(this.palette.color(p));
  }

  // Drain the grid's dirty chunks into the mesher's pending queue and rebuild up
  // to `budget` of them this call, returning true if any remain. Bounding the
  // per-frame work keeps a huge edit (commit a 128³ box fill, Fill/Clear the
  // canvas) from blocking the main thread for seconds — the caller wakes the
  // render loop while pending, so the rest rebuilds over the next frames and the
  // app stays interactive. budget defaults to Infinity (drain fully in one call,
  // for tests / non-interactive callers).
  //
  // `alsoDirty`: extra chunk keys to treat as dirty this call. The grid's
  // dirty-chunk queue is ONE-SHOT with a single consumer; when the raymarch
  // volume sync drains it first (both renderers stay warm for the zoom-based
  // hot swap), the render loop hands the drained set on through here.
  private pending = new Set<number>();
  sync(grid: VoxelGrid, msBudget = Infinity, alsoDirty?: Iterable<number>): boolean {
    const dirty = grid.consumeDirtyChunks();
    if (alsoDirty) for (const ck of alsoDirty) dirty.add(ck);

    // Worker path: hand the changed chunks' bytes to the worker and return
    // whether anything is still in flight. Geometry is applied later, in the
    // worker's onmessage. msBudget is irrelevant — the work isn't on this thread.
    if (this.worker) {
      if (dirty.size > 0) {
        const chunks: ChunkUpdate[] = [];
        for (const ck of dirty) {
          const [cx, cy, cz] = parseChunkKey(ck);
          const c = grid.getChunkStorage(cx, cy, cz);
          // Not transferred: structured-clone copies the bytes so the editing
          // grid keeps its own (it's still the source of truth for picking/save).
          chunks.push(
            c
              ? { key: ck, cells: c.cells, count: c.count }
              : { key: ck, cells: null, count: 0 },
          );
          this.outstanding.add(ck);
        }
        this.worker.postMessage({ chunks });
      }
      return this.outstanding.size > 0;
    }

    for (const ck of dirty) this.pending.add(ck);
    if (this.pending.size === 0) return false;
    // Time-based (not count-based) budget: cheap rebuilds — a Clear's chunk
    // teardowns, sparse chunks — drain by the hundred per frame, while a dense
    // Fill's costly chunks process only a handful, so both stay near the budget
    // instead of a fixed count that's too slow for Clear and too janky for Fill.
    const timed = msBudget !== Infinity;
    const start = timed ? performance.now() : 0;
    let n = 0;
    for (const ck of this.pending) {
      this.rebuildChunk(grid, ck);
      this.pending.delete(ck); // deleting the current key mid-iteration is safe
      // Check the clock every few chunks so performance.now() isn't the cost.
      if (timed && (++n & 7) === 0 && performance.now() - start >= msBudget) break;
    }
    return this.pending.size > 0;
  }

  // Inject pre-built, already-merged geometry as a single synthetic chunk — the
  // baked-asset deserialize path (model-bake's bakedAssetToObject3D). Reuses the
  // exact palette materials + grid/edge line materials a live mesh uses, so a
  // deserialized placement is identical to a freshly-meshed one with no material
  // drift. Buffers may be typed arrays (zero-copy) or number[].
  applyBakedGeometry(
    faces: ReadonlyArray<FaceBuffers>,
    gridLines: ArrayLike<number>,
    edgeLines: ArrayLike<number>,
  ): void {
    this.applyChunkGeometry(0, faces, gridLines, edgeLines);
  }

  // Drop ALL geometry + tell the worker to forget its mirror. Pair with the
  // grid's markAllChunksDirty() and a sync to rebuild from scratch — used when
  // this mesher was dormant (the raymarcher was the active renderer) so its
  // worker mirror is stale and may still hold chunks that were deleted since.
  reset(): void {
    for (const ck of [...this.chunks.keys()]) this.teardownChunk(ck);
    this.outstanding.clear();
    this.pending.clear();
    this.worker?.postMessage({ chunks: [], reset: true });
  }

  // Tear a chunk's meshes down and drop it from the map (no cells, or fully
  // interior so nothing is visible). Idempotent — a no-op if absent.
  private teardownChunk(ck: number): void {
    const chunkMesh = this.chunks.get(ck);
    if (!chunkMesh) return;
    for (const mesh of chunkMesh.faces.values()) mesh.geometry.dispose();
    chunkMesh.gridLines.geometry.dispose();
    chunkMesh.edgeLines.geometry.dispose();
    this._group.remove(chunkMesh.group);
    this.chunks.delete(ck);
  }

  // Synchronous rebuild (no-worker path): compute on this thread, then apply.
  private rebuildChunk(grid: VoxelGrid, ck: number): void {
    const geo = buildChunkGeometry(grid, ck);
    if (!geo) {
      this.teardownChunk(ck);
      return;
    }
    const faces: FaceBuffers[] = [];
    for (const [palette, b] of geo.faces) {
      faces.push({ palette, pos: b.pos, norm: b.norm, idx: b.idx });
    }
    this.applyChunkGeometry(ck, faces, geo.gridLines, geo.edgeLines);
  }

  // Apply computed geometry to this chunk's Three.js meshes. Shared by the
  // synchronous rebuild and the worker's result handler; buffers may be number[]
  // or typed arrays (posAttr/idxAttr wrap each appropriately).
  private applyChunkGeometry(
    ck: number,
    faces: ReadonlyArray<FaceBuffers>,
    gridLines: ArrayLike<number>,
    edgeLines: ArrayLike<number>,
  ): void {
    let chunkMesh = this.chunks.get(ck);
    if (!chunkMesh) {
      chunkMesh = this.createChunkMesh();
      this.chunks.set(ck, chunkMesh);
      this._group.add(chunkMesh.group);
    }

    // Drop face meshes for palettes no longer present in the chunk.
    const present = new Set<number>();
    for (const f of faces) present.add(f.palette);
    for (const [p, mesh] of chunkMesh.faces) {
      if (!present.has(p)) {
        chunkMesh.group.remove(mesh);
        mesh.geometry.dispose();
        chunkMesh.faces.delete(p);
      }
    }

    // Add or refresh face meshes per palette present in the chunk.
    for (const f of faces) {
      let mesh = chunkMesh.faces.get(f.palette);
      if (!mesh) {
        mesh = new THREE.Mesh(
          new THREE.BufferGeometry(),
          this.getPaletteMaterial(f.palette),
        );
        // Presentation rig (floor-mode placed models): models throw and catch
        // sun shadows. Inert in the editor — its scenes have no shadow light.
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        chunkMesh.faces.set(f.palette, mesh);
        chunkMesh.group.add(mesh);
      } else {
        (mesh.material as THREE.MeshStandardMaterial).color.copy(
          this.palette.color(f.palette),
        );
        mesh.geometry.dispose();
        mesh.geometry = new THREE.BufferGeometry();
      }
      const g = mesh.geometry;
      g.setAttribute("position", posAttr(f.pos, 3));
      g.setAttribute("normal", posAttr(f.norm, 3));
      g.setIndex(idxAttr(f.idx));
      g.computeBoundingSphere();
    }

    chunkMesh.gridLines.geometry.dispose();
    chunkMesh.gridLines.geometry = new THREE.BufferGeometry().setAttribute(
      "position",
      posAttr(gridLines, 3),
    );
    chunkMesh.edgeLines.geometry.dispose();
    chunkMesh.edgeLines.geometry = new THREE.BufferGeometry().setAttribute(
      "position",
      posAttr(edgeLines, 3),
    );
  }

  private createChunkMesh(): ChunkMesh {
    const group = new THREE.Group();

    const gridLines = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      this.gridMat,
    );
    gridLines.renderOrder = 10;
    gridLines.visible = this.gridLinesVisible;
    group.add(gridLines);

    const edgeLines = new THREE.LineSegments(
      new THREE.BufferGeometry(),
      this.edgeMat,
    );
    edgeLines.renderOrder = 11;
    edgeLines.visible = this.edgesVisible;
    group.add(edgeLines);

    return { group, faces: new Map(), gridLines, edgeLines };
  }

  private getPaletteMaterial(p: PaletteIndex): THREE.MeshStandardMaterial {
    let mat = this.paletteMats.get(p);
    if (!mat) {
      mat = new THREE.MeshStandardMaterial({
        color: this.palette.color(p),
        roughness: 0.85,
        metalness: 0.0,
        // Bias the face triangles slightly backward in the depth
        // buffer so coplanar layers drawn on top of them — grid
        // lines, edge lines, selection overlay — reliably win the
        // depth test instead of pixel-fighting due to float
        // interpolation across the triangle. WebGL only honors
        // polygonOffset for fill primitives (not lines), so the bias
        // lives on the face mat rather than on the line mats. The bias
        // is well under a cell's depth, so back-face lines never bleed
        // through the ≥1-cell-thick walls in front of them.
        polygonOffset: true,
        polygonOffsetFactor: 2,
        polygonOffsetUnits: 2,
      });
      this.applyHighlight(mat);
      this.paletteMats.set(p, mat);
    }
    return mat;
  }
}

export interface PaletteLookup {
  color(p: PaletteIndex): THREE.Color;
}
